// System Health — live infra dashboard.
//
// Source-of-truth reference: /var/www/kitchenos/admin-standalone.html
//   page section: ~line 4025 (#system-health-section, refreshHealthDashboard)
//
// Backends:
//   GET /api/health                — EXISTS (server.js ~341). Returns:
//                                    { ok, service, status, node_version,
//                                      uptime_s, memory:{rss_mb,heap_used_mb,
//                                      heap_total_mb}, pm2:{...},
//                                      active_retailers, services:{brevo,
//                                      twilio, stripe, openai}, last_error,
//                                      last_test_run, timestamp }
//   GET /api/admin/system-health   — INTENDED for aggregated stats
//                                    (OpenAI calls today, Stripe webhook
//                                    backlog, error rate, recent errors).
//                                    Currently absent — we degrade gracefully.
//
// Auto-refresh every 30s. Cleanup aborts the in-flight fetch and clears the
// interval. No demo data is fabricated for the missing aggregated endpoint —
// those cards render "—".

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

interface HealthResponse {
  ok?: boolean;
  service?: string;
  status?: string;
  node_version?: string;
  model?: string;
  uptime_s?: number;
  memory?: { rss_mb?: number; heap_used_mb?: number; heap_total_mb?: number; heap_limit_mb?: number };
  pm2?: { status?: string; restarts?: number; uptime_ms?: number; pid?: number };
  active_retailers?: number;
  services?: Record<string, boolean>;
  last_error?: { message?: string; endpoint?: string; timestamp?: string } | null;
  last_test_run?: { ok?: boolean; passed?: number; failed?: number; ranAt?: string } | null;
  timestamp?: string;
}

interface AdminHealthExtras {
  openai_calls_today?: number;
  stripe_webhook_backlog?: number;
  error_rate_last_hour?: number;
  last_deployment?: string;
  postgres_ok?: boolean;
  ai_render_ok?: boolean;
  recent_errors?: Array<{ message?: string; endpoint?: string; timestamp?: string }>;
}

interface ServiceRow {
  key: string;
  label: string;
  ok: boolean;
  detail: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function fmtUptime(s: number | undefined): string {
  if (s == null || !Number.isFinite(s)) return "—";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h < 24) return `${h}h ${m}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function fmtTimestamp(iso: string | undefined): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return iso;
  return new Date(t).toLocaleString("en-GB");
}

// ── Page ─────────────────────────────────────────────────────────────────────

const systemHealthPage: Page = {
  name: "system-health",
  label: "System Health",
  icon: "❤️",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    let ac = new AbortController();
    let refreshTimer: number | null = null;
    let health: HealthResponse | null = null;
    let extras: AdminHealthExtras | null = null;
    let extrasMissing = false;
    let loading = true;
    let error: string | null = null;

    const root = document.createElement("section");
    root.className = "asys-page";
    root.innerHTML = `
      <header class="asys-header">
        <div>
          <h1>System Health</h1>
          <p>Live status of the kitchenos-api process and its external services. Auto-refreshes every 30s.</p>
        </div>
        <div class="asys-head-actions">
          <span class="asys-last-update" data-role="last-update"></span>
          <button class="adm-btn adm-btn-ghost" type="button" data-role="refresh">Refresh now</button>
          <a class="adm-btn adm-btn-ghost" href="/api/health" target="_blank" rel="noopener">Raw JSON</a>
        </div>
      </header>

      <div class="asys-grid" data-role="stats"></div>

      <div class="adm-card">
        <h3 class="asys-section-h">Services</h3>
        <div class="asys-services" data-role="services"></div>
      </div>

      <div class="adm-card">
        <h3 class="asys-section-h">Recent errors</h3>
        <div data-role="errors"></div>
      </div>
    `;
    container.appendChild(root);

    const statsEl = root.querySelector<HTMLDivElement>("[data-role='stats']")!;
    const servicesEl = root.querySelector<HTMLDivElement>("[data-role='services']")!;
    const errorsEl = root.querySelector<HTMLDivElement>("[data-role='errors']")!;
    const updEl = root.querySelector<HTMLSpanElement>("[data-role='last-update']")!;
    const refreshBtn = root.querySelector<HTMLButtonElement>("[data-role='refresh']")!;

    function statCard(label: string, value: string, tone: "ok" | "warn" | "danger" | "info" | "muted" = "muted"): string {
      return `
        <div class="asys-stat asys-stat-${tone}">
          <div class="asys-stat-label">${esc(label)}</div>
          <div class="asys-stat-value">${esc(value)}</div>
        </div>
      `;
    }

    function renderStats(): void {
      if (loading && !health) {
        statsEl.innerHTML = `<div class="adm-loading">Loading…</div>`;
        return;
      }
      if (error && !health) {
        statsEl.innerHTML = `<div class="adm-error">${esc(error)}</div>`;
        return;
      }
      const h = health ?? {};
      const apiOk = (h.status ?? "").toLowerCase() === "up" || h.ok === true;
      const pm2Ok = h.pm2?.status === "online";
      const uptimeStr = fmtUptime(h.uptime_s);
      // Gauge heap usage against the real V8 ceiling (heap_size_limit), not the
      // committed heap (heap_total) — the latter always tracks close to used and
      // produces a misleading "near 100%" reading. Fall back to committed heap
      // only when an older API hasn't reported heap_limit_mb yet.
      const heapUsed = h.memory?.heap_used_mb ?? null;
      const heapLimit = h.memory?.heap_limit_mb ?? h.memory?.heap_total_mb ?? null;
      const heapPct = heapUsed != null && heapLimit ? Math.round((heapUsed / heapLimit) * 100) : null;
      const memStr = heapUsed != null
        ? `${heapUsed} / ${heapLimit ?? "?"} MB${heapPct != null ? ` (${heapPct}%)` : ""}`
        : "—";
      const heapTone: "ok" | "warn" | "danger" | "info" | "muted" =
        heapPct == null ? "info" : heapPct >= 90 ? "danger" : heapPct >= 75 ? "warn" : "ok";
      const openaiCalls = extras?.openai_calls_today;
      const stripeBacklog = extras?.stripe_webhook_backlog;
      const errRate = extras?.error_rate_last_hour;
      const lastDeploy = extras?.last_deployment;
      statsEl.innerHTML = [
        statCard("API uptime", uptimeStr, apiOk ? "ok" : "danger"),
        statCard("PM2 status", h.pm2?.status ?? "—", pm2Ok ? "ok" : h.pm2?.status ? "warn" : "muted"),
        statCard("Active retailers", String(h.active_retailers ?? "—"), "info"),
        statCard("Heap", memStr, heapTone),
        statCard(
          "OpenAI calls today",
          openaiCalls != null ? String(openaiCalls) : extrasMissing ? "—" : "…",
          openaiCalls != null ? "info" : "muted",
        ),
        statCard(
          "Stripe webhook backlog",
          stripeBacklog != null ? String(stripeBacklog) : extrasMissing ? "—" : "…",
          stripeBacklog != null && stripeBacklog > 0 ? "warn" : "muted",
        ),
        statCard(
          "Error rate (1h)",
          errRate != null ? `${errRate.toFixed(2)}/min` : extrasMissing ? "—" : "…",
          errRate != null && errRate > 1 ? "danger" : errRate != null ? "ok" : "muted",
        ),
        statCard(
          "Last deployment",
          lastDeploy ? fmtTimestamp(lastDeploy) : extrasMissing ? "—" : "…",
          "muted",
        ),
      ].join("");
    }

    function renderServices(): void {
      const h = health ?? {};
      const svc = h.services ?? {};
      const rows: ServiceRow[] = [
        {
          key: "kitchenos-api",
          label: "kitchenos-api",
          ok: (h.status ?? "").toLowerCase() === "up" || h.ok === true,
          detail: h.pm2?.status ? `pm2: ${h.pm2.status}` : "",
        },
        {
          key: "postgres",
          label: "PostgreSQL",
          ok: extras?.postgres_ok ?? true,
          detail: extras?.postgres_ok == null ? "(not probed)" : "",
        },
        {
          key: "ai-render",
          label: "ai-render",
          ok: extras?.ai_render_ok ?? true,
          detail: extras?.ai_render_ok == null ? "(not probed)" : "",
        },
        { key: "twilio", label: "Twilio", ok: !!svc.twilio, detail: svc.twilio ? "configured" : "not configured" },
        { key: "stripe", label: "Stripe", ok: !!svc.stripe, detail: svc.stripe ? "configured" : "not configured" },
        { key: "openai", label: "OpenAI", ok: !!svc.openai, detail: svc.openai ? "configured" : "not configured" },
        { key: "brevo", label: "Brevo email", ok: !!svc.brevo, detail: svc.brevo ? "configured" : "not configured" },
      ];
      servicesEl.innerHTML = rows
        .map(
          (r) => `
        <div class="asys-svc">
          <span class="asys-svc-dot asys-svc-dot-${r.ok ? "ok" : "danger"}"></span>
          <span class="asys-svc-label">${esc(r.label)}</span>
          <span class="asys-svc-detail">${esc(r.detail)}</span>
          <span class="adm-pill ${r.ok ? "adm-pill-ok" : "adm-pill-danger"}">${r.ok ? "OK" : "DOWN"}</span>
        </div>`,
        )
        .join("");
    }

    function renderErrors(): void {
      const recent = extras?.recent_errors ?? [];
      const fallback = health?.last_error ? [health.last_error] : [];
      const list = recent.length > 0 ? recent : fallback;
      if (list.length === 0) {
        errorsEl.innerHTML = `<div class="adm-empty">No recent errors</div>`;
        return;
      }
      errorsEl.innerHTML = `
        <ul class="asys-err-list">
          ${list
            .slice(0, 20)
            .map(
              (e) => `
            <li class="asys-err">
              <div class="asys-err-msg">${esc(e?.message ?? "(no message)")}</div>
              <div class="asys-err-meta">
                ${e?.endpoint ? `<span>${esc(e.endpoint)}</span>` : ""}
                ${e?.timestamp ? `<span>${esc(fmtTimestamp(e.timestamp))}</span>` : ""}
              </div>
            </li>`,
            )
            .join("")}
        </ul>
        ${recent.length === 0 ? `<p class="asys-note">Showing last error from <code>/api/health</code>. A dedicated <code>/api/admin/system-health</code> endpoint would expose the full 20-item log.</p>` : ""}
      `;
    }

    function renderAll(): void {
      renderStats();
      renderServices();
      renderErrors();
      updEl.textContent = health?.timestamp
        ? `Updated ${fmtTimestamp(health.timestamp)}`
        : "";
    }

    async function fetchHealth(signal: AbortSignal): Promise<void> {
      try {
        health = await ctx.api<HealthResponse>("/health", { signal });
      } catch (err) {
        if (isAbortError(err)) throw err;
        error = err instanceof ApiError ? `Couldn't reach /api/health (${err.status}).` : "Couldn't reach /api/health.";
      }
    }

    async function fetchExtras(signal: AbortSignal): Promise<void> {
      try {
        extras = await ctx.api<AdminHealthExtras>("/admin/system-health", { signal });
        extrasMissing = false;
      } catch (err) {
        if (isAbortError(err)) throw err;
        if (err instanceof ApiError && err.status === 404) {
          extras = null;
          extrasMissing = true;
        }
        // For any error we just leave extras as previous value.
      }
    }

    async function refresh(): Promise<void> {
      try {
        await fetchHealth(ac.signal);
        await fetchExtras(ac.signal);
      } catch (err) {
        if (isAbortError(err)) return;
      }
      loading = false;
      renderAll();
    }

    function scheduleNext(): void {
      if (refreshTimer != null) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(async () => {
        ac = new AbortController();
        await refresh();
        scheduleNext();
      }, 30_000);
    }

    const onRefresh = (): void => {
      ac.abort();
      ac = new AbortController();
      loading = true;
      renderAll();
      void refresh().then(scheduleNext);
    };
    refreshBtn.addEventListener("click", onRefresh);

    void refresh().then(scheduleNext);
    renderAll();

    return () => {
      ac.abort();
      if (refreshTimer != null) window.clearTimeout(refreshTimer);
      refreshBtn.removeEventListener("click", onRefresh);
      root.remove();
    };
  },
};

registerPage(systemHealthPage);
export default systemHealthPage;
